import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Bus, WarningCircle, Pause, Play, GraduationCap, Broadcast, CheckCircle } from '@phosphor-icons/react';
import Layout from '../../components/Layout';
import MapErrorBoundary from '../../components/MapErrorBoundary';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';
import transportService from '../../services/transportService';
import { animateMarkerTo, setMarkerHeading, stopMarkerAnimation, pushBreadcrumbPoint } from '../../utils/mapAnimation';
import './security.css';

export default function DriverTrackingPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const routeIdParam = searchParams.get('route');

  const [routes, setRoutes]             = useState([]);
  const [routeId, setRouteId]           = useState(routeIdParam || '');
  const [route, setRoute]               = useState(null);
  const [students, setStudents]         = useState([]);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [loadError, setLoadError]       = useState(null);

  const [position, setPosition]     = useState(null);
  const [gpsError, setGpsError]     = useState(null);
  const [sharing, setSharing]       = useState(true);
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const [boardedIds, setBoardedIds] = useState({});
  const [activeTab, setActiveTab]   = useState('students');
  const [postCount, setPostCount]   = useState(0);
  const [sessionActive, setSessionActive]   = useState(false);
  const [activating, setActivating]         = useState(false);
  const [activationError, setActivationError] = useState(null);

  const mapRef          = useRef(null);
  const mapInstanceRef  = useRef(null);
  const markerRef       = useRef(null);
  const trailRef        = useRef(null);   // L.polyline breadcrumb of this driver's own path
  const pointsRef       = useRef([]);     // [[lat,lng], ...]
  const leafletRef      = useRef(null);
  const watchIdRef      = useRef(null);
  const postIntervalRef = useRef(null);

  const busIconHtml = () => `<div style="position:relative;width:30px;height:30px;">
      <div style="font-size:26px;line-height:1;filter:drop-shadow(0 2px 8px rgba(245,158,11,0.8));text-align:center;">🚌</div>
      <div class="map-heading-rotor" style="position:absolute;bottom:-4px;left:50%;margin-left:-6px;width:12px;height:12px;">
        <svg viewBox="0 0 12 12" width="12" height="12"><polygon points="6,0 11,11 6,8 1,11" fill="#f59e0b" stroke="white" stroke-width="0.5"/></svg>
      </div>
    </div>`;

  // Lazily creates the driver's own marker + breadcrumb trail on the first
  // real GPS fix (never at a placeholder coordinate), then glides/rotates
  // it on every fix after that.
  const upsertOwnMarker = useCallback((L, map, lat, lng, heading) => {
    if (!markerRef.current) {
      const busIcon = L.divIcon({ html: busIconHtml(), className: 'bus-marker-icon', iconSize: [30, 30], iconAnchor: [15, 15] });
      markerRef.current = L.marker([lat, lng], { icon: busIcon }).addTo(map);
      pointsRef.current = [[lat, lng]];
      trailRef.current = L.polyline([[lat, lng]], { color: '#f59e0b', weight: 2, opacity: 0.45, dashArray: '4 6' }).addTo(map);
      map.setView([lat, lng], 15);
    } else {
      animateMarkerTo(markerRef.current, [lat, lng]);
      setMarkerHeading(markerRef.current, heading);
      pointsRef.current = pushBreadcrumbPoint(pointsRef.current, [lat, lng]);
      trailRef.current?.setLatLngs(pointsRef.current);
    }
  }, []);

  // Load routes for this school
  useEffect(() => {
    if (!user?.school_id) return;
    transportService.listRoutes({ school_id: user.school_id })
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setRoutes(list);
        // Driver role: auto-select first route if none specified
        if (!routeId && list.length > 0) setRouteId(String(list[0].id));
      })
      .catch(() => {});
  }, [user?.school_id]);

  // Load route details + students
  useEffect(() => {
    if (!routeId) return;
    setLoadingRoute(true);
    setLoadError(null);
    Promise.all([
      transportService.getRoute(routeId),
      transportService.getRouteStudents(routeId),
    ])
      .then(([routeData, studentData]) => {
        setRoute(routeData);
        setStudents(Array.isArray(studentData) ? studentData : []);
      })
      .catch(() => setLoadError('Could not load route data.'))
      .finally(() => setLoadingRoute(false));
  }, [routeId, user?.school_id]);

  // Activate today's tracking session for this route before any GPS posting
  // is allowed — the backend rejects location posts for a route that hasn't
  // been activated (or that this driver isn't actually assigned to).
  useEffect(() => {
    if (!routeId) return;
    setSessionActive(false);
    setActivationError(null);
    setActivating(true);
    securityService.activateTransportSession(routeId)
      .then(() => setSessionActive(true))
      .catch(err => setActivationError(err.message || 'Could not start tracking session for this route.'))
      .finally(() => setActivating(false));
  }, [routeId]);

  // Elapsed timer
  useEffect(() => {
    const t = setInterval(() => setElapsedSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Real GPS watch
  useEffect(() => {
    if (!sharing || !navigator.geolocation) {
      if (!navigator.geolocation) setGpsError('Geolocation not supported on this device.');
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      pos => {
        const { latitude: lat, longitude: lng, heading } = pos.coords;
        setPosition({
          lat, lng,
          speed_kmh: pos.coords.speed ? Math.round(pos.coords.speed * 3.6) : null,
          // heading is only populated by most devices while actually moving —
          // null (not 0) when stationary or unsupported, so we pass it through
          // as-is rather than defaulting it.
          heading: heading === null || Number.isNaN(heading) ? null : heading,
        });
        setGpsError(null);
        // Marker/camera updates happen in the `position`-driven effect below
        // — kept out of this callback so each fix only triggers one update.
      },
      err => setGpsError(`GPS: ${err.message}`),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
    );
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, [sharing]);

  // Post location to backend every 10s
  useEffect(() => {
    if (!sharing || !position || !routeId || !sessionActive) return;
    clearInterval(postIntervalRef.current);
    postIntervalRef.current = setInterval(() => {
      securityService.postTransportLocation(routeId, {
        lat: position.lat,
        lng: position.lng,
        speed_kmh: position.speed_kmh,
        heading: position.heading,
      }).then(() => setPostCount(c => c + 1)).catch(() => {});
    }, 10000);
    return () => clearInterval(postIntervalRef.current);
  }, [sharing, position, routeId, sessionActive]);

  // Init map
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    let isMounted = true;
    import('leaflet').then(L => {
      if (!isMounted || !mapRef.current || mapInstanceRef.current) return;
      if (!document.querySelector('#leaflet-css')) {
        const link = document.createElement('link');
        link.id = 'leaflet-css'; link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      // No marker is placed yet — a real GPS fix (either already in hand or
      // arriving via the effect below) is what puts the bus icon on the
      // map, so it's never shown sitting at this placeholder center.
      const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false })
        .setView([5.5560, -0.1870], 13);
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(map);

      leafletRef.current = L;
      mapInstanceRef.current = map;
      // If GPS already had a fix before the map was ready, place the marker now
      setPosition(prev => {
        if (prev) upsertOwnMarker(L, map, prev.lat, prev.lng, prev.heading);
        return prev;
      });
    });
    return () => {
      isMounted = false;
      stopMarkerAnimation(markerRef.current);
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; markerRef.current = null; }
    };
  }, [upsertOwnMarker]);

  // Move marker if position arrives after the map is already ready
  useEffect(() => {
    if (position && leafletRef.current && mapInstanceRef.current) {
      const hadMarker = !!markerRef.current;
      upsertOwnMarker(leafletRef.current, mapInstanceRef.current, position.lat, position.lng, position.heading);
      if (hadMarker) {
        mapInstanceRef.current.panTo([position.lat, position.lng], { animate: true, duration: 1 });
      }
    }
  }, [position, upsertOwnMarker]);

  const handleToggleSharing = useCallback(() => {
    if (sharing && watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      clearInterval(postIntervalRef.current);
    }
    setSharing(v => !v);
  }, [sharing]);

  const formatElapsed = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return (
    <Layout>
    <div className="driver-page">
      {/* Header */}
      <div className="driver-header">
        <div style={{ fontSize: 28, marginRight: 4 }}><Bus size={28} /></div>
        <div className="driver-header-info">
          <h2>{route?.name || 'Select Route'}</h2>
          <p>
            {route?.vehicle_plate || '—'} · {students.length} students
            {position && ` · ${position.speed_kmh !== null ? `${position.speed_kmh} km/h` : 'GPS active'}`}
          </p>
        </div>
        {sharing && position && (
          <div className="driver-sharing-badge">
            <div className="driver-sharing-dot" />
            Live · {postCount} sent
          </div>
        )}

        {/* Route selector */}
        {routes.length > 1 && (
          <select
            value={routeId}
            onChange={e => setRouteId(e.target.value)}
            style={{
              marginLeft: 12, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 6, color: 'white', padding: '6px 10px', fontSize: 12, outline: 'none',
            }}
          >
            {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        )}
      </div>

      {/* GPS / load / activation errors */}
      {(gpsError || loadError || activationError) && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fca5a5', padding: '10px 16px',
          fontSize: 12, color: '#dc2626', display: 'flex', gap: 8, alignItems: 'center',
        }}>
          <WarningCircle size={16} /> {activationError || gpsError || loadError}
        </div>
      )}

      {!activationError && activating && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', padding: '10px 16px',
          fontSize: 12, color: '#92400e', display: 'flex', gap: 8, alignItems: 'center',
        }}>
          Starting tracking session for this route…
        </div>
      )}

      {/* Map */}
      <MapErrorBoundary minHeight={340}>
        <div id="driver-map" ref={mapRef} style={{ flex: 1, minHeight: 340 }} />
      </MapErrorBoundary>

      {/* Bottom sheet */}
      <div className="driver-bottom-sheet">
        {/* Coords bar */}
        <div className="driver-coords-bar">
          {position ? (
            <>
              <div className="driver-coord-item">
                <span className="driver-coord-label">LAT</span>
                <span className="driver-coord-value">{position.lat.toFixed(6)}°</span>
              </div>
              <span className="driver-coord-sep">|</span>
              <div className="driver-coord-item">
                <span className="driver-coord-label">LNG</span>
                <span className="driver-coord-value">{position.lng.toFixed(6)}°</span>
              </div>
              <span className="driver-coord-sep">|</span>
              <div className="driver-coord-item">
                <span className="driver-coord-label">SPD</span>
                <span className="driver-coord-value">
                  {position.speed_kmh !== null ? `${position.speed_kmh} km/h` : '— km/h'}
                </span>
              </div>
              <span className="driver-coord-sep">|</span>
            </>
          ) : (
            <span style={{ color: '#9ca3af', fontSize: 11 }}>
              {sharing ? 'Getting GPS fix...' : 'Location sharing paused'}
            </span>
          )}
          <div className="driver-coord-item">
            <span className="driver-coord-label">TIME</span>
            <span className="driver-coord-value">{formatElapsed(elapsedSecs)}</span>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button
              onClick={handleToggleSharing}
              style={{
                padding: '4px 10px', borderRadius: 8, border: 'none', fontSize: 11, fontWeight: 600,
                cursor: 'pointer', background: sharing ? '#fef3c7' : '#16a34a', color: sharing ? '#92400e' : 'white',
              }}
            >
              {sharing
                ? <><Pause size={11} weight="fill" /> Pause</>
                : <><Play size={11} weight="fill" /> Resume</>}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="driver-bottom-tabs">
          <button className={`driver-bottom-tab ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}>
            <GraduationCap size={16} /> Students ({students.length})
          </button>
          <button className={`driver-bottom-tab ${activeTab === 'sharing' ? 'active' : ''}`}
            onClick={() => setActiveTab('sharing')}>
            <Broadcast size={16} /> Sharing
          </button>
        </div>

        {/* Tab content */}
        <div className="driver-bottom-content">
          {activeTab === 'students' && (
            <div>
              {loadingRoute && (
                <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                  Loading students...
                </div>
              )}
              {!loadingRoute && students.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
                  No students enrolled on this route.
                </div>
              )}
              {students.map(s => {
                const isBoarded = boardedIds[s.id];
                return (
                  <div key={s.id} className="driver-student-row">
                    <div
                      className={`driver-checkbox ${isBoarded ? 'checked' : ''}`}
                      onClick={() => setBoardedIds(prev => ({ ...prev, [s.id]: !prev[s.id] }))}
                    >
                      {isBoarded ? <CheckCircle size={14} weight="fill" /> : ''}
                    </div>
                    <span className="driver-student-name">
                      {s.first_name} {s.last_name}
                    </span>
                    <span className="driver-student-stop">
                      {s.drop_off_location || s.destination || ''}
                    </span>
                    <span style={{ fontSize: 11, color: isBoarded ? '#16a34a' : '#d97706' }}>
                      {isBoarded ? 'On Board' : 'Waiting'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === 'sharing' && (
            <div style={{ paddingTop: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Live Location Sharing</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {sharing ? `Broadcasting to parents · ${postCount} updates sent` : 'Paused'}
                  </div>
                </div>
                <div
                  onClick={handleToggleSharing}
                  style={{
                    width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
                    background: sharing ? '#16a34a' : '#d1d5db', position: 'relative', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', background: 'white',
                    position: 'absolute', top: 3, transition: 'left 0.2s',
                    left: sharing ? 22 : 3,
                  }} />
                </div>
              </div>
              {gpsError && (
                <div style={{ padding: 12, background: '#fee2e2', borderRadius: 8, fontSize: 12, color: '#dc2626', marginBottom: 12 }}>
                  {gpsError}
                </div>
              )}
              {position && (
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#6b7280', lineHeight: 1.8 }}>
                  <div>Lat: {position.lat.toFixed(6)}</div>
                  <div>Lng: {position.lng.toFixed(6)}</div>
                  {position.speed_kmh !== null && <div>Speed: {position.speed_kmh} km/h</div>}
                </div>
              )}
              <div style={{ marginTop: 12, fontSize: 11, color: '#9ca3af' }}>
                {students.length} parent{students.length !== 1 ? 's' : ''} can see your live location while sharing is active.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </Layout>
  );
}
